import type { JSX } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
import TRANSITION from '@integrations/pint-appstore-commons/src/enums/APP_STATUS_TRANSITION.json';
import AppStatusChooser from '../appStatusChooser/AppStatusChooser';
import { DesktopAppDefaults } from './DesktopAppDefaults';
import { CollaborateDefaults } from './CollaborateDefaults';
import { TrustedLocation } from './TrustedLocation';
import { DeviceSetup } from './DeviceSetup';
import ConfirmationModal from '../../common/ConfirmationModal';
import { CoeditPolicy, CoeditPolicies } from '../../editors/editorsReducer';
import { AppId, SelectedGroup } from '../../common/interfaces';
import { changeAppSettings, updateCoeditPolicy } from '../../common/appStatusActionCreators';
import { CoEditingSettings, CoEditOption, CollaborateSettings } from './interfaces';
import { getCoeditPolicyFromSelectValue } from './MSOfficeDesktopModalUtils';
import GroupsChooser from '../groupsChooser/GroupsChooser';

interface MSOfficeDesktopEditModalProps {
    name: string;
    transition: any;
    areGroupsHidden: boolean;
    shouldBeAppStatusChooserDisabled: boolean;
    coeditPolicy: CoeditPolicy;
    collaborationAppsHierarchy?: AppId[];
    dispatchChangeAppSettings: typeof changeAppSettings;
    dispatchUpdateCoeditPolicy: typeof updateCoeditPolicy;
    appId: string;
    onClose: () => void;
    title: JSX.Element;
    areGroupsFetching: boolean;
    selectedGroups: SelectedGroup[];
    searchBarCache: any;
}

// Helper function to map policy to co-edit option
const getCoeditSettingFromPolicy = (policy: CoeditPolicies): CoEditOption => {
    switch (policy) {
        case CoeditPolicies.AlwaysCoedit:
            return 'Start co-editing automatically';
        case CoeditPolicies.Ask:
            return 'Ask the user';
        case CoeditPolicies.NeverCoedit:
            return 'Open file without starting co-editing';
        default:
            return 'Start co-editing automatically';
    }
};

export const MSOfficeDesktopEditModal = ({
    name,
    transition,
    areGroupsHidden,
    shouldBeAppStatusChooserDisabled,
    coeditPolicy,
    collaborationAppsHierarchy,
    dispatchChangeAppSettings,
    dispatchUpdateCoeditPolicy,
    appId,
    onClose,
    title,
    areGroupsFetching,
    selectedGroups,
    searchBarCache,
}: MSOfficeDesktopEditModalProps) => {
    const [activeTab, setActiveTab] = useState('general');
    const [localTransition, setLocalTransition] = useState(transition);
    const [localSelectedGroups, setLocalSelectedGroups] = useState<SelectedGroup[]>(selectedGroups || []);
    const [hasSelectedGroupsChanged, setHasSelectedGroupsChanged] = useState(false);
    const [coEditingSettings, setCoEditingSettings] = useState<CoEditingSettings>({
        word: getCoeditSettingFromPolicy(coeditPolicy.word),
        excel: getCoeditSettingFromPolicy(coeditPolicy.excel),
        powerPoint: getCoeditSettingFromPolicy(coeditPolicy.powerpoint),
        allowOverwrite: coeditPolicy.userCanOverwrite,
    });
    const [collaborateSettings, setCollaborateSettings] = useState<CollaborateSettings>({
        makeDefaultCoEditing: collaborationAppsHierarchy?.[0] === appId,
    });

    const [hasChanges, setHasChanges] = useState(false);
    const shouldDisplayGroupsChooser = localTransition === TRANSITION.GROUP;

    // Effect to determine if there are any changes in settings
    useEffect(() => {
        const hasCoEditingChanges =
            coEditingSettings.word !== getCoeditSettingFromPolicy(coeditPolicy.word) ||
            coEditingSettings.excel !== getCoeditSettingFromPolicy(coeditPolicy.excel) ||
            coEditingSettings.powerPoint !== getCoeditSettingFromPolicy(coeditPolicy.powerpoint) ||
            coEditingSettings.allowOverwrite !== coeditPolicy.userCanOverwrite;

        const hasCollaborateChanges =
            collaborateSettings.makeDefaultCoEditing !== (collaborationAppsHierarchy?.[0] === appId);

        const hasTransitionChanges = localTransition !== transition;

        setHasChanges(hasCoEditingChanges || hasCollaborateChanges || hasTransitionChanges || hasSelectedGroupsChanged);
    }, [
        coEditingSettings,
        collaborateSettings,
        localTransition,
        coeditPolicy,
        collaborationAppsHierarchy,
        appId,
        hasSelectedGroupsChanged,
    ]);

    // Handle transition change
    const handleTransitionChange = useCallback((newTransition: string) => {
        setLocalTransition(newTransition);
    }, []);

    const handleSelectedGroupChange = useCallback((groups: SelectedGroup[]) => {
        setLocalSelectedGroups([...groups]);
        setHasSelectedGroupsChanged(true);
    }, []);

    // Map co-edit option to policy
    const getCoeditPolicy = useCallback(getCoeditPolicyFromSelectValue, []);

    useEffect(() => {
        setLocalSelectedGroups(selectedGroups || []);
    }, [selectedGroups]);

    // Handle confirmation of changes
    const handleConfirm = useCallback(() => {
        if (localTransition === TRANSITION.DISABLE) {
            const appsHierarchy = collaborationAppsHierarchy?.filter((id) => id !== appId) || [];
            Promise.resolve(
                dispatchChangeAppSettings(
                    {
                        appId,
                        status: { transition: localTransition, groups: [] },
                        editors: {
                            collaborationAppsHierarchy: appsHierarchy,
                            collaborationAppsPrioritizationEnabled: appsHierarchy.length > 0,
                        },
                    },
                    false
                )
            ).then(() => {
                onClose();
            });
        } else {
            const appsHierarchy = collaborateSettings.makeDefaultCoEditing
                ? [appId, ...(collaborationAppsHierarchy?.filter((id) => id !== appId) || [])]
                : collaborationAppsHierarchy?.filter((id) => id !== appId) || [];

            Promise.resolve(
                dispatchChangeAppSettings(
                    {
                        appId,
                        status: {
                            transition: localTransition,
                            groups: localTransition === TRANSITION.GROUP ? localSelectedGroups.map((x) => x.id) : [],
                        },
                        editors: {
                            collaborationAppsHierarchy: appsHierarchy,
                            collaborationAppsPrioritizationEnabled: appsHierarchy.length > 0,
                        },
                    },
                    collaborateSettings.makeDefaultCoEditing
                )
            ).then(() => {
                Promise.resolve(
                    dispatchUpdateCoeditPolicy({
                        arePoliciesEnabled: true,
                        userCanOverwrite: coEditingSettings.allowOverwrite,
                        excel: getCoeditPolicy(coEditingSettings.excel),
                        word: getCoeditPolicy(coEditingSettings.word),
                        powerpoint: getCoeditPolicy(coEditingSettings.powerPoint),
                    })
                ).then(() => {
                    onClose();
                });
            });
        }
    }, [
        appId,
        collaborateSettings,
        collaborationAppsHierarchy,
        coEditingSettings,
        dispatchChangeAppSettings,
        dispatchUpdateCoeditPolicy,
        getCoeditPolicy,
        localTransition,
        onClose,
        localSelectedGroups,
        hasSelectedGroupsChanged,
    ]);

    // Render content based on active tab
    const renderTabContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <>
                        <AppStatusChooser
                            name={name}
                            transition={localTransition}
                            handleChange={handleTransitionChange}
                            hideGroups={areGroupsHidden}
                            disabled={shouldBeAppStatusChooserDisabled}
                        />
                        {shouldDisplayGroupsChooser && (
                            <GroupsChooser
                                isLoading={areGroupsFetching}
                                isHidden={!shouldDisplayGroupsChooser}
                                selectedGroups={localSelectedGroups}
                                handleChange={handleSelectedGroupChange}
                                searchBarCache={searchBarCache}
                            />
                        )}
                    </>
                );

            case 'app-defaults':
                return (
                    <>
                        <DesktopAppDefaults
                            coEditingSettings={coEditingSettings}
                            setCoEditingSettings={setCoEditingSettings}
                        />
                        <div className="border1p mt1" />
                        <CollaborateDefaults
                            collaborateSettings={collaborateSettings}
                            setCollaborateSettings={setCollaborateSettings}
                            showPreview={false}
                        />
                    </>
                );

            case 'user-setup':
                return (
                    <>
                        <DeviceSetup />
                        <TrustedLocation />
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <ConfirmationModal
            className="ast-app-status-form"
            title={title}
            confirmLabel="Save"
            cancelLabel="Cancel"
            confirmHandler={handleConfirm}
            closeHandler={onClose}
            childrenWrapperClassName="ast-app-status-form__content"
            isDisabled={!hasChanges}
            hideCloseButton
        >
            <div className="ms-office-tabs">
                <div
                    className={`ms-office-tab ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                >
                    General
                </div>
                <div
                    className={`ms-office-tab ${activeTab === 'app-defaults' ? 'active' : ''}`}
                    onClick={() => setActiveTab('app-defaults')}
                >
                    App defaults
                </div>
                <div
                    className={`ms-office-tab ${activeTab === 'user-setup' ? 'active' : ''}`}
                    onClick={() => setActiveTab('user-setup')}
                >
                    User setup
                </div>
            </div>

            {renderTabContent()}
        </ConfirmationModal>
    );
};

export default MSOfficeDesktopEditModal;
